import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Chart } from 'chart.js';
import { ActivatedRoute, Router } from '@angular/router';

import { GlobalService } from 'src/app/app-core/lib/global.service';
import { DashboardService } from 'src/app/app-core/services/dashboard.service';
import { DepartmentService } from 'src/app/app-core/services/department.service';
import { UserService } from 'src/app/app-core/services/user.service';
import { OrganizationService } from 'src/app/app-core/services/organization.service';
import { IDepartment } from 'src/app/interfaces/department';
import { IListScopeDepartmentResponse } from 'src/app/interfaces/listScope/listScopeDepartmentResponse';
import { FilterService, IDashboardFilters } from 'src/app/app-core/services/filter.service';
import { Dropdown } from 'primeng/dropdown';
import { IOrganization } from 'src/app/interfaces/organization';
import { IListScopeOrganizationResponse } from 'src/app/interfaces/listScope/listScopeOrganizationResponse';
import { GerinusBD } from 'src/app/app-core/lib/gerinus-bd.component';
import { Observable, forkJoin } from 'rxjs';
import { ProcessService } from 'src/app/app-core/services/process.service';
import { ApplicantRequestService } from 'src/app/app-core/services/applicant-request.service';
import { RequestService } from 'src/app/app-core/services/request.service';

@Component({
  selector: 'dashboard-admin',
  templateUrl: './dashboard-admin.component.html',
  styleUrls: ['../../pages/dashboard/dashboard.component.scss', './dashboard-admin.component.scss'],
  providers: [
    DashboardService,
    UserService,
    FilterService,
    OrganizationService,
    ProcessService
  ],
})
export class DashboardAdminComponent extends GerinusBD implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    public global: GlobalService,
    private activatedRoute: ActivatedRoute,
    public dashboardService: DashboardService,
    private filterService: FilterService,
    private router: Router,
    public departmentService: DepartmentService,
    public userService: UserService,
    public organizationService: OrganizationService,
    public processService: ProcessService,
    public requestService: RequestService,
    public applicantRequestService: ApplicantRequestService
  ) {
    super(global, dashboardService);
  }

  public requestsAmount: number;
  public processAmount: number;
  public organizationsAmount: number;
  public usersAmount: number;
  public screeningProcess: number;
  public unassignedProcesses: number;
  // FILTROS
  public isFiltersModalOpen: boolean = false;
  public departments: IDepartment[];
  public selectedDepartments: any;
  public dashboardFilters: IDashboardFilters;
  public organizations: IOrganization[] | undefined;
  public selectedOrganization: string;
  public organizationSelectedName = '';
  public departmentSelectedName = '';
  public availableCards = [
    { label: 'Total de Solicitações', value: 'requestsAmount' },
    { label: 'Total de Processos', value: 'processAmount' },
    { label: 'Total de Órgãos', value: 'organizationsAmount' },
    { label: 'Total de Usuários', value: 'usersAmount' },
    { label: 'Processos em Triagem', value: 'screeningProcess' },
    { label: 'Processos sem Atribuição', value: 'unassignedProcesses' },
  ];

  public tempSelectedCards: string[] = this.availableCards.map(card => card.value);
  public selectedCards: string[] = [...this.tempSelectedCards];
  public isFilterApplied: boolean = false;

  @ViewChild('requestsChart') requestsChartRef: ElementRef<HTMLCanvasElement>;
  private requestsChart: Chart;

  private readonly monthLabels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  private pendingRequestsPerMonth?: number[];

  ngAfterViewInit() {
    if (this.pendingRequestsPerMonth) {
      this.renderRequestsChart(this.monthLabels, this.pendingRequestsPerMonth);
    }
  }

  // VARIAÇÃO MENSAL — percentual de cada card em relação ao mês anterior, pela chave do card
  public monthlyVariation: Record<string, number | null> = {};

  private readonly variationKeys = {
    requestsAmount: 'solicitacoes',
    processAmount: 'processos',
    organizationsAmount: 'orgaos',
    usersAmount: 'usuarios',
    screeningProcess: 'processosTriagem',
    unassignedProcesses: 'processosSemAtribuicao',
  };

  public getMonthlyVariation() {
    this.monthlyVariation = {};

    this.dashboardService
      .getVariacaoMensal({
        orgao: this.selectedOrganization,
        departamento: this.selectedDepartments,
      })
      .subscribe({
        next: (response: any) => {
          const variation: Record<string, number | null> = {};
          for (const [card, key] of Object.entries(this.variationKeys)) {
            variation[card] = response?.[key]?.percentual ?? null;
          }
          this.monthlyVariation = variation;
        },
      });
  }

  /** Sem variação calculável (carregando, erro ou mês anterior zerado) a badge não aparece. */
  public hasVariation(card: string): boolean {
    return typeof this.monthlyVariation[card] === 'number';
  }

  public formatVariation(card: string): string {
    const value = this.monthlyVariation[card] as number;
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1).replace('.', ',')}%`;
  }

  // ALERTAS
  public processAlerts: { vencidos: number; proximosDoVencimento: number; semAtribuicao: number; emAndamento: number } | undefined;

  public getProcessAlerts() {
    this.processAlerts = undefined;

    this.dashboardService
      .getAlertasProcessos({
        orgao: this.selectedOrganization,
        departamento: this.selectedDepartments,
      })
      .subscribe({
        next: (response: any) => (this.processAlerts = response),
        error: () => (this.processAlerts = { vencidos: 0, proximosDoVencimento: 0, semAtribuicao: 0, emAndamento: 0 }),
      });
  }

  // ATIVIDADE RECENTE
  public recentActivities: { tipo: string; numero: string; historico: string; data: string }[] | undefined;

  private readonly activityTypeLabels = {
    solicitacao: 'Solicitação',
    processo: 'Processo',
    processo_interno: 'Processo interno',
  };

  public getRecentActivities() {
    this.recentActivities = undefined;

    this.dashboardService.getAtividadesRecentes().subscribe({
      next: (response: any) => (this.recentActivities = response?.atividades ?? []),
      error: () => (this.recentActivities = []),
    });
  }

  public activityReference(activity: { tipo: string; numero: string }): string {
    const label = this.activityTypeLabels[activity.tipo] ?? '';
    return activity.numero ? `${label} Nº ${activity.numero}` : label;
  }

  /** "agora", "5 min atrás", "3 h atrás", "2 dias atrás"; acima de 30 dias, a data. */
  public timeAgo(date: string): string {
    const seconds = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 1000));

    if (seconds < 60) return 'agora';

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min atrás`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} h atrás`;

    const days = Math.floor(hours / 24);
    if (days <= 30) return `${days} ${days == 1 ? 'dia' : 'dias'} atrás`;

    return new Date(date).toLocaleDateString('pt-BR');
  }

  // ÓRGÃOS MAIS ATIVOS
  public mostActiveOrganizations: { codigo: number; descricao: string; sigla: string; total: number; percentual: number }[] | undefined;

  public getMostActiveOrganizations() {
    this.mostActiveOrganizations = undefined;

    this.dashboardService.getOrgaosMaisAtivos().subscribe({
      next: (response: any) => (this.mostActiveOrganizations = response?.orgaos ?? []),
      error: () => (this.mostActiveOrganizations = []),
    });
  }

  public formatPercent(value: number): string {
    return `${value.toFixed(1).replace('.', ',')}%`;
  }

  public getRequestsPerMonth(year?: number) {
    this.dashboardService
      .getSolicitacoesPorMes({
        ano: year,
        orgao: this.selectedOrganization,
        departamento: this.selectedDepartments,
      })
      .subscribe({
        next: (response: any) => this.showRequestsPerMonth(response?.totais ?? new Array(12).fill(0)),
        error: () => this.showRequestsPerMonth(new Array(12).fill(0)),
      });
  }

  private showRequestsPerMonth(values: number[]) {
    if (!this.requestsChartRef) {
      this.pendingRequestsPerMonth = values;
      return;
    }

    this.renderRequestsChart(this.monthLabels, values);
  }

  ngOnDestroy() {
    this.requestsChart?.destroy();
  }

  private renderRequestsChart(labels: string[], values: number[]) {
    this.requestsChart?.destroy();

    this.requestsChart = new Chart(this.requestsChartRef.nativeElement, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Solicitações',
            data: values,
            borderColor: '#5e72e4',
            backgroundColor: 'rgba(94, 114, 228, 0.1)',
            borderWidth: 2,
            pointBackgroundColor: '#5e72e4',
            pointRadius: 3,
            pointHoverRadius: 5,
            lineTension: 0.4,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (item) => `${item.yLabel} ${item.yLabel == 1 ? 'solicitação' : 'solicitações'}`,
          },
        },
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, gridLines: { color: '#e5e7eb', zeroLineColor: '#e5e7eb' } }],
        },
      },
    });
  }

  async ngOnInit() {
    this.getData();
    this.getMostActiveOrganizations();
    this.getRecentActivities();
    this.getDepartments();
    this.getOrganizations();
  }

  public getData() {
    this.activatedRoute.queryParams.subscribe(params => {
      this.selectedDepartments = params['department'] || '';
      this.selectedOrganization = params['organization'] || '';

      let whereProcess = '';
      let whereOrganization = '';
      let whereUsers = '';
      let whereUnassignedProcess = '';
      let whereScreeningProcess = '';
      let whereProcessDepartments = '';
      let whereProcessDepartmentsScreeningProcess = '';
      let whereProcessDepartmentsUnassignedProcess = '';
      // a solicitação não tem departamento: filtra pelo departamento responsável pelo assunto,
      // o mesmo critério do gráfico de solicitações por mês
      let whereRequestDepartment = '';

      if (this.selectedOrganization) {
        whereProcess += ` AND "SOL_CodigoOrgao" = ${this.selectedOrganization} `;
        whereOrganization += `"OR_CodigoOrgao" = ${this.selectedOrganization}`;
        whereUsers += `"USR_CodigoOrgao" = ${this.selectedOrganization}`;
        whereUnassignedProcess += `"SOL_CodigoOrgao" = ${this.selectedOrganization}`;
        whereScreeningProcess += `"SOL_CodigoOrgao" = ${this.selectedOrganization}`;
      }

      if (this.selectedDepartments) {
        whereProcessDepartments += ` AND "PRT_DepartamentoAtual" = ${this.selectedDepartments} `;
        whereUsers += ` AND "PE_CodigoDepartamento" = ${this.selectedDepartments}`;
        whereProcessDepartmentsScreeningProcess += ` AND "PRT_DepartamentoAtual" = ${this.selectedDepartments} `;
        whereProcessDepartmentsUnassignedProcess += ` AND "PRT_DepartamentoAtual" = ${this.selectedDepartments} `;
        whereRequestDepartment += ` AND "AS_CodigoDepartamento" = ${this.selectedDepartments} `;
      }

      const finalWhereScreeningProcess = whereProcessDepartmentsScreeningProcess && whereScreeningProcess
        ? `${whereScreeningProcess} ${whereProcessDepartmentsScreeningProcess}`
        : whereScreeningProcess + whereProcessDepartmentsScreeningProcess;

      const finalWhereUnassignedProcess = whereProcessDepartmentsUnassignedProcess && whereUnassignedProcess
        ? `${whereUnassignedProcess} ${whereProcessDepartmentsUnassignedProcess} AND `
        : whereProcessDepartmentsUnassignedProcess;

      this.getRequestsPerMonth();
      this.getProcessAlerts();
      this.getMonthlyVariation();

      this.getCount(
        'processAmount',
        'processService',
        `"PRT_DataFim" IS NULL ${whereProcess} ${whereProcessDepartments}`,
      );

      this.getCount(
        'requestsAmount',
        'requestService',
        `"SOL_CodigoSolicitacao" IS NOT NULL ${whereProcess} ${whereRequestDepartment}`
      );

      this.getCount(
        'organizationsAmount',
        'organizationService',
        whereOrganization
      );

      this.getCount(
        'usersAmount',
        'userService',
        whereUsers
      );

      const combinedWhere = finalWhereScreeningProcess
      ? `"createdByGroup" = 'TRIAGEM' AND (${finalWhereScreeningProcess})`
      : `"createdByGroup" = 'TRIAGEM'`;

      this.getCountScreeningUnassignedProcess(
        'screeningProcess',
        'processService',
        combinedWhere
      );

      this.getCountScreeningUnassignedProcess(
        'unassignedProcesses',
        'processService',
        whereProcessDepartmentsUnassignedProcess
          ? finalWhereUnassignedProcess + `"PRC_CodigoResponsavel" IS NULL`
          : `"PRC_CodigoResponsavel" IS NULL`
      );
    });
  }

  public getCountScreeningUnassignedProcess(variableName: string, service: string, where = '') {
    this.dashboardService.getTotalObservableScreeningProcess(service, where).subscribe({
      // O `map` que existia aqui montava objetos com `createdByGroup` e jogava fora: só o
      // tamanho da lista era aproveitado. A contagem agora vem do próprio paginador.
      next: (response: any) => {
        this[variableName] = response?.meta?.total ?? response?.data?.length ?? 0;
      }
    });
  }

  public getCount(variableName: string, service: string, where = '') {
    this.dashboardService.getTotalObservable(service, where).subscribe({
      next: (response: any) => {
        this[variableName] = response?.meta?.total ?? response?.data?.length ?? 0;
      }
    });
  }

  public filter() {
    this.dashboardFilters = {
      department: this.selectedDepartments,
      organization: this.selectedOrganization,
    };

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: this.dashboardFilters,
      queryParamsHandling: 'merge',
    });

    if (this.tempSelectedCards.length === 0) {
      this.selectedCards = this.availableCards.map(card => card.value);
    } else {
      this.selectedCards = [...this.tempSelectedCards];
    }
    this.isFilterApplied = true;

    this.listAll();

  }

  clearFilters() {
    this.selectedDepartments = null;
    this.selectedOrganization = null;
    this.tempSelectedCards = [];
    this.selectedCards = [];
    this.isFilterApplied = false;

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {},
    });

    this.listAll();
    this.isFiltersModalOpen = false;
  }

  fetchAllDashboardFiltered() {
    this.selectedCards = [...this.tempSelectedCards];
    this.listAll();
    this.filter();
    this.isFiltersModalOpen = false;
  }

  getDepartments() {
    let where = '';
    if (this.selectedOrganization) {
      where = `"DEP_CodigoOrgao" = ${this.selectedOrganization}`;
    }

    this.departmentService.listAll(where, 0).subscribe({
      next: (response: IListScopeDepartmentResponse) => {
        this.departments = response.data;
      }
    });
  }

  getOrganizations() {
    this.organizationService.listAll('', 0).subscribe({
      next: (response: IListScopeOrganizationResponse) => {
        this.organizations = response.data;
      }
    });
  }

  restoreFilterValues() {
    const org = this.organizations.find(org => org.OR_CodigoOrgao == this.selectedOrganization);
    this.organizationSelectedName = org ? org.OR_Descricao : '';

    const dep = this.departments.find(dep => dep.DEP_CodigoDepartamento == parseInt(this.selectedDepartments, 10));
    this.departmentSelectedName = dep ? dep.DEP_Descricao : '';

    this.selectedOrganization = org?.OR_CodigoOrgao || null;
    this.selectedDepartments = dep?.DEP_CodigoDepartamento || null;
  }

  changeOrganization() {
    const organizationName = this.organizations.find(org => org.OR_CodigoOrgao == this.selectedOrganization);
    this.organizationSelectedName = organizationName.OR_Descricao;
    this.getDepartments();
  }

  changeDepartaments() {
    const dapartmentName = this.departments.find(dep => dep.DEP_CodigoDepartamento == parseInt(this.selectedDepartments, 10));
    this.departmentSelectedName = dapartmentName.DEP_Descricao;
  }

  public isCardVisible(card: string): boolean {
    if (!this.isFilterApplied || this.selectedCards.length === 0) {
      return true;
    }

    return this.selectedCards.includes(card);
  }

  private combineWhere(conditionA: string, conditionB: string): string {
    if (conditionA && conditionB) {
      return `${conditionA} AND (${conditionB})`;
    } else if (conditionA) {
      return conditionA;
    } else if (conditionB) {
      return conditionB;
    }
    return '';
  }

  generateDashboardListPDF() {
    const selected = this.selectedCards;

    const filtersUSR = this.selectedOrganization && this.selectedDepartments
      ? `"OR_CodigoOrgao" = ${this.selectedOrganization} AND "DEP_CodigoDepartamento" = ${this.selectedDepartments}`
      : '';

    const filtersOR = this.selectedOrganization
      ? `"OR_CodigoOrgao" = ${this.selectedOrganization}`
      : '';

    const filtersUP = this.selectedDepartments
      ? `"PRT_DepartamentoAtual" = ${this.selectedDepartments} AND "PRC_CodigoResponsavel" IS NULL`
      : '';

    const filtersSP = this.selectedDepartments
      ? `"PRT_DepartamentoAtual" = ${this.selectedDepartments} AND "PRC_NumeroProcesso" IS NOT NULL`
      : '';

    const filtersPRC = this.selectedDepartments
      ? `"PRT_DepartamentoAtual" = ${this.selectedDepartments}`
      : '';

    let filtersSOL = this.selectedOrganization
      ? `"SOL_CodigoOrgao" = ${this.selectedOrganization} AND "SOL_CodigoSolicitacao" IS NOT NULL`
      : `"SOL_CodigoSolicitacao" IS NOT NULL`;

    if (this.selectedDepartments) {
      filtersSOL += ` AND "AS_CodigoDepartamento" = ${this.selectedDepartments}`;
    }

    const cards: any = {};

    if (selected.includes('usersAmount')) {
      cards.usuario = this.userService.listAll(filtersUSR, 0);
    }

    if (selected.includes('organizationsAmount')) {
      cards.orgao = this.organizationService.listAll(filtersOR, 0);
    }

    if (selected.includes('unassignedProcesses')) {
      const combinedUP = this.combineWhere(filtersPRC, `"PRC_CodigoResponsavel" IS NULL`);
      cards.unassignedProcess = this.processService.listAll(combinedUP, 0);
    }

    if (selected.includes('screeningProcess')) {
      const combinedSP = this.combineWhere(`"createdByGroup" = 'TRIAGEM'`, filtersSP);
      cards.screeningProcess = this.processService.listAll(combinedSP, 0);
    }

    if (selected.includes('requestsAmount')) {
      cards.requests = this.requestService.listAll(filtersSOL, 0);
    }

    if (selected.includes('processAmount')) {
      cards.process = this.processService.listAll(filtersPRC, 0);
    }

    forkJoin(cards).subscribe({
      next: (response: any) => {
        this.dashboardService.generateListPDF(response);
      },
    });
  }
}